
import React, { useState } from 'react';
import { ShieldCheck, CheckCircle2, ClipboardCheck, ShieldAlert, Lock, BrainCircuit, UserCheck, AlertCircle, Activity } from 'lucide-react';
import { LGPD_CHECKLIST_DOCUMENT } from '../content/legalDocuments';

interface LGPDChecklistProps {
  onAccept: () => void;
}

const LGPDChecklist: React.FC<LGPDChecklistProps> = ({ onAccept }) => {
  const [agreed, setAgreed] = useState(false);

  // Redação integral fornecida pelo Product Owner (bloco 31):
  // content/legalDocuments.ts é a única fonte, compartilhada com o Repositório.
  const icons = [
    <UserCheck size={18} className="text-indigo-500" />,
    <ClipboardCheck size={18} className="text-emerald-500" />,
    <Lock size={18} className="text-blue-500" />,
    <BrainCircuit size={18} className="text-purple-500" />,
    <ShieldCheck size={18} className="text-cyan-500" />,
    <Activity size={18} className="text-amber-500" />,
    <AlertCircle size={18} className="text-orange-500" />,
  ];
  const sections = LGPD_CHECKLIST_DOCUMENT.sections.map((section, idx) => ({
    title: section.heading,
    icon: icons[idx % icons.length],
    items: section.items,
  }));

  return (
    <div className="fixed inset-0 bg-slate-950 z-[105] flex items-center justify-center p-4 md:p-8 overflow-hidden font-sans">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_30%,rgba(16,185,129,0.08),transparent_70%)]"></div>
      </div>

      <div className="bg-white dark:bg-slate-900 w-full max-w-4xl max-h-[90vh] rounded-[2rem] sm:rounded-[3rem] shadow-2xl border dark:border-slate-800 flex flex-col relative overflow-hidden animate-in slide-in-from-bottom-8 duration-500">
        <div className="p-5 sm:p-8 border-b dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex items-center gap-4">
          <div className="p-3 bg-emerald-600 rounded-2xl text-white shadow-lg shadow-emerald-200 dark:shadow-none">
            <ShieldAlert size={28} />
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">{LGPD_CHECKLIST_DOCUMENT.title}</h2>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">{LGPD_CHECKLIST_DOCUMENT.subtitle}</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 sm:p-8 md:p-12 space-y-10 text-slate-700 dark:text-slate-300 custom-scrollbar bg-white dark:bg-slate-900">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-sm font-medium leading-relaxed bg-slate-50 dark:bg-slate-800/40 p-6 rounded-3xl border dark:border-slate-700 mb-10">
              {LGPD_CHECKLIST_DOCUMENT.intro}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {sections.map((section, idx) => (
                <div key={idx} className="space-y-4 animate-in fade-in slide-in-from-left-4 duration-500" style={{ animationDelay: `${idx * 100}ms` }}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-xl">{section.icon}</div>
                    <h4 className="text-xs font-black text-slate-900 dark:text-white uppercase tracking-wider">{section.title}</h4>
                  </div>
                  <ul className="space-y-3">
                    {section.items.map((item, iIdx) => (
                      <li key={iIdx} className={`flex gap-3 text-xs leading-relaxed group ${item.startsWith('- ') ? 'pl-7' : ''}`}>
                        {!item.startsWith('- ') && (
                          <div className="mt-0.5 w-4 h-4 rounded border border-slate-300 dark:border-slate-600 flex items-center justify-center shrink-0 bg-slate-50 dark:bg-slate-800 group-hover:border-emerald-500 transition-colors">
                            <CheckCircle2 size={10} className="text-emerald-500 opacity-50" />
                          </div>
                        )}
                        <span className="text-slate-600 dark:text-slate-400 font-medium">{item.startsWith('- ') ? item.slice(2) : item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-16 p-5 sm:p-8 bg-emerald-50 dark:bg-emerald-950/20 rounded-[2.5rem] border border-emerald-100 dark:border-emerald-900/30">
              <h5 className="text-sm font-black uppercase tracking-widest text-emerald-800 dark:text-emerald-300 mb-6 flex items-center gap-2">
                <ShieldCheck size={20} /> {LGPD_CHECKLIST_DOCUMENT.declaration.title}
              </h5>
              <div className="space-y-4 text-xs leading-relaxed text-emerald-900 dark:text-emerald-200 font-bold">
                <p>{LGPD_CHECKLIST_DOCUMENT.declaration.intro}</p>
                <ol className="list-decimal pl-5 space-y-2">
                  {LGPD_CHECKLIST_DOCUMENT.declaration.items.map((item, idx) => (
                    <li key={idx}>{item.replace(/^\d+\.\s*/, '')}</li>
                  ))}
                </ol>
                <p className="mt-6 pt-4 border-t border-emerald-200/50 text-[10px] opacity-70">{LGPD_CHECKLIST_DOCUMENT.support}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-8 border-t dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex flex-col md:flex-row items-center justify-between gap-6">
          <label className="flex items-center gap-3 cursor-pointer group">
            <div 
              onClick={() => setAgreed(!agreed)}
              className={`w-7 h-7 rounded-xl border-2 flex items-center justify-center transition-all ${agreed ? 'bg-emerald-600 border-emerald-600 shadow-lg shadow-emerald-200' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'}`}
            >
              {agreed && <CheckCircle2 size={18} className="text-white" />}
            </div>
            <span className="text-[11px] font-black text-slate-600 dark:text-slate-400 uppercase tracking-widest group-hover:text-emerald-600 transition-colors">Confirmo a ciência e responsabilidade sobre os itens acima</span>
          </label>
          
          <button 
            onClick={onAccept}
            disabled={!agreed}
            className="w-full md:w-auto px-6 sm:px-12 py-4 bg-emerald-600 text-white rounded-2xl font-black uppercase tracking-[0.2em] text-xs shadow-xl shadow-emerald-200 dark:shadow-none hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
          >
            Finalizar Aceite
          </button>
        </div>
      </div>
    </div>
  );
};

export default LGPDChecklist;
